import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import axiosInstance from "../api/axiosConfig";
import Logo from "../assets/images/favicon.png";
import emailjs from '@emailjs/browser';

const DonationSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const transactionId = searchParams.get("transaction_id");
  const [loading, setLoading] = useState(true);
  const [transaction, setTransaction] = useState(null);
  const [error, setError] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [emailSending, setEmailSending] = useState(false);

  // Initialize EmailJS with error handling
  useEffect(() => {
    try {
      emailjs.init("m1av0nxwNr_eOMQy_");
      console.log("EmailJS initialized successfully");
    } catch (error) {
      console.error("EmailJS initialization failed:", error);
    }
  }, []);

  useEffect(() => {
    if (!transactionId) {
      const pending = sessionStorage.getItem("pendingDonation");
      if (pending) {
        try {
          const data = JSON.parse(pending);
          console.log("Found pending donation:", data);
          fetchTransaction(data.transaction_id);
        } catch (error) {
          console.error("Failed to parse pending donation:", error);
          navigate("/donation");
        }
      } else {
        console.log("No transaction ID and no pending donation");
        navigate("/donation");
      }
    } else {
      console.log("Fetching transaction with ID:", transactionId);
      fetchTransaction(transactionId);
    }
  }, [transactionId]);

  const sendConfirmationEmail = async (transactionData) => {
    // Check if email already sent for this transaction
    const emailSentKey = `email_sent_${transactionData.transaction?.transaction_id || transactionId}`;
    const alreadySent = sessionStorage.getItem(emailSentKey);
    
    if (alreadySent) {
      console.log("Email already sent for this transaction");
      setEmailSent(true);
      return;
    }

    setEmailSending(true);
    
    try {
      // Extract donor email from transaction data
      const donorEmail = transactionData.donor?.email || 
                        transactionData.transaction?.donor_email ||
                        transactionData.email;
      
      const donorName = transactionData.donor?.first_name 
        ? `${transactionData.donor.first_name} ${transactionData.donor.last_name || ''}`
        : 'Valued Donor';
      
      let donationAmount = transactionData.transaction?.amount || 
                          transactionData.amount;
      
      if (!donationAmount && transactionData.transaction?.amount_raw) {
        donationAmount = parseFloat(transactionData.transaction.amount_raw).toFixed(2);
      }
      
      if (donorEmail) {
        console.log("Sending confirmation email to:", donorEmail);
        
        // Prepare email template parameters
        const templateParams = {
          to_email: donorEmail,
          to_name: donorName,
          donation_amount: typeof donationAmount === 'number' ? `$${donationAmount.toFixed(2)}` : donationAmount,
          donation_for: transactionData.transaction?.donation_for || 'General Donation',
          transaction_id: transactionData.transaction?.transaction_id || transactionId,
          donation_date: new Date().toLocaleDateString('en-AU', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          }),
          payment_method: transactionData.transaction?.donation_mode || 'Card',
          receipt_url: transactionData.receipt?.download_url || '',
          temple_name: 'Sydney Sri Ayyappa Swami Centre',
          support_email: 'info@sydneysriyayappa.com',
          support_phone: '02 9631 2800',
          year: new Date().getFullYear()
        };

        // Send email using EmailJS
        const result = await emailjs.send(
          'service_unq437t',
          'template_27p63b9',
          templateParams
        );
        
        console.log("Confirmation email sent successfully to:", donorEmail, result);
        setEmailSent(true);
        // Mark email as sent to prevent duplicate sends
        sessionStorage.setItem(emailSentKey, 'true');
      } else {
        console.warn("No donor email found, skipping email send");
      }
    } catch (emailError) {
      console.error("Error sending confirmation email:", emailError);
      // Don't set main error, just log it - don't disrupt user experience
    } finally {
      setEmailSending(false);
    }
  };

  const fetchTransaction = async (id) => {
    try {
      console.log("Fetching transaction status for ID:", id);
      const response = await axiosInstance.get(
        `/donations/stripe/status/${id}`,
      );
      console.log("Transaction status response:", response.data);
      
      if (response.data.success) {
        const transactionData = response.data.data;
        setTransaction(transactionData);
        
        // Send email after successfully fetching transaction
        await sendConfirmationEmail(transactionData);
      } else {
        console.error("Transaction not found for ID:", id);
        setError("Transaction not found");
      }
    } catch (err) {
      console.error("Failed to load transaction details:", err);
      setError("Failed to load transaction details");
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = async () => {
    const idToUse = transactionId || transaction?.transaction?.transaction_id;
    
    if (!idToUse) {
      console.error("No transaction ID available for retry");
      setError("Transaction ID not found. Please contact support.");
      return;
    }

    setRetrying(true);
    setError(null);
    
    try {
      console.log("Attempting to generate receipt for transaction ID:", idToUse);
      
      // Try different possible endpoint patterns
      let response;
      try {
        // Try the original endpoint first
        response = await axiosInstance.post(
          `/donations/stripe/generate-receipt/${idToUse}`
        );
      } catch (firstError) {
        console.warn("First endpoint failed, trying alternative...", firstError);
        
        // Try alternative endpoint patterns
        const alternativeEndpoints = [
          `/donations/generate-receipt/${idToUse}`,
          `/donations/receipt/${idToUse}/generate`,
          `/donations/stripe/receipt/${idToUse}`,
          `/donations/${idToUse}/generate-receipt`
        ];
        
        let alternativeSuccess = false;
        for (const endpoint of alternativeEndpoints) {
          try {
            console.log(`Trying alternative endpoint: ${endpoint}`);
            response = await axiosInstance.post(endpoint);
            alternativeSuccess = true;
            break;
          } catch (altError) {
            console.warn(`Alternative endpoint ${endpoint} failed:`, altError);
          }
        }
        
        if (!alternativeSuccess) {
          throw new Error("All receipt generation endpoints failed");
        }
      }
      
      console.log("Receipt generation response:", response.data);
      
      if (response.data.success) {
        // Refresh transaction data to get the new receipt
        await fetchTransaction(idToUse);
        
        // If receipt URL is available, open it
        if (response.data.data?.download_url) {
          console.log("Opening receipt URL:", response.data.data.download_url);
          window.open(response.data.data.download_url, '_blank');
        } else if (response.data.download_url) {
          console.log("Opening receipt URL:", response.data.download_url);
          window.open(response.data.download_url, '_blank');
        } else {
          console.warn("No download URL in response");
          setError("Receipt generated but download URL not available");
        }
      } else {
        console.error("Receipt generation failed:", response.data);
        setError(response.data.message || "Failed to generate receipt. Please contact support.");
      }
    } catch (err) {
      console.error("Error regenerating receipt:", err);
      
      // Provide more specific error messages
      if (err.response?.status === 404) {
        setError("Receipt generation endpoint not found. Please contact support with your Transaction ID: " + idToUse);
      } else if (err.response?.status === 500) {
        setError("Server error while generating receipt. Please try again later.");
      } else {
        setError("Could not generate receipt. Please try again later or contact support.");
      }
    } finally {
      setRetrying(false);
    }
  };

  if (loading) {
    return (
      <div className="container py-5 text-center">
        <div className="spinner-border text-warning mb-3"></div>
        <p>Loading transaction details...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container py-5 text-center">
        <i className="bi bi-exclamation-triangle-fill text-danger fs-1 mb-3"></i>
        <h3>Error</h3>
        <p className="text-muted">{error}</p>
        {error.includes("Transaction ID:") && (
          <p className="small text-muted mt-2">Please save this Transaction ID for reference</p>
        )}
        <button
          className="btn btn-warning text-white rounded-pill px-4 mt-3"
          onClick={() => navigate("/donation")}
        >
          Return to Donation Page
        </button>
      </div>
    );
  }

  return (
    <div className="position-relative" style={{ minHeight: "100vh" }}>
      <div
        className="position-absolute top-0 start-0 w-100"
        style={{ height: "30%", backgroundColor: "#4b0000" }}
      ></div>
      <div
        className="position-absolute bottom-0 start-0 w-100 bg-light"
        style={{ height: "70%" }}
      ></div>

      <div
        className="position-absolute top-50 start-50 translate-middle w-100 px-3"
        style={{ maxWidth: "600px", zIndex: 10 }}
      >
        <div className="card shadow-lg border-0 rounded-4 overflow-hidden">
          {/* Card Header */}
          <div className="card-header border-0 text-center pt-4 pb-3 px-4">
            <img
              src={Logo}
              alt="Sydney Sri Ayyappa Swami Centre"
              style={{ height: "70px", objectFit: "contain" }}
              className="mb-3"
            />
            <h2 className="fw-bold mb-2">Thank You for Your Donation!</h2>
            <p className="text-muted mb-0">
              Your generous support helps us serve the community better.
            </p>
          </div>

          {/* Card Body */}
          <div className="card-body px-4 py-4">
            <div className="bg-light p-4 rounded-4 mb-4">
              <h5 className="fw-bold mb-4">Donation Details</h5>

              <div className="row align-items-center">
                <div className="col-md-6">
                  {transaction?.transaction?.amount && (
                    <div className="mb-3">
                      <small className="text-muted d-block">Amount</small>
                      <strong className="fs-5 heading">
                        {transaction?.transaction?.amount ||
                          (transaction?.transaction?.amount_raw
                            ? `$${parseFloat(transaction.transaction.amount_raw).toFixed(2)}`
                            : "N/A")}{" "}
                      </strong>
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  {transaction?.transaction?.donation_for && (
                    <div>
                      <small className="text-muted d-block">Donation For</small>
                      <strong>
                        {transaction?.transaction?.donation_for || "Donation"}
                      </strong>
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  {transactionId && (
                    <div className="mb-3">
                      <small className="text-muted d-block">
                        Transaction ID
                      </small>
                      <strong className="text-break">
                        {transaction?.transaction?.transaction_id ||
                          transactionId}
                      </strong>
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  {transaction?.receipt?.available &&
                  transaction?.receipt?.download_url ? (
                    <a
                      href={transaction.receipt.download_url}
                      target="_blank"
                      rel="noreferrer"
                      className="btn btn-outline-warning btn rounded-pill px-4"
                    >
                      <i className="bi bi-file-earmark-arrow-down me-2"></i>
                      Download Receipt
                    </a>
                  ) : (
                    <>
                      <button
                        onClick={handleRetry}
                        disabled={retrying}
                        className="btn btn-outline-warning btn rounded-pill px-4"
                      >
                        {retrying ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                            Generating...
                          </>
                        ) : (
                          <>
                            <i className="bi bi-arrow-repeat me-2"></i>
                            Retry Generating Receipt
                          </>
                        )}
                      </button>
                      <p className="text-muted small mt-2 mb-0">
                        Click to try generating your receipt again
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="text-center"></div>
          </div>

          {/* Card Footer */}
          <div className="card-footer border-0 p-0">
            <div className="bg-light px-4 py-4 text-center">
              <div className="d-flex justify-content-center gap-4 flex-wrap">
                <Link
                  to="/"
                  className="btn btn-outline-secondary px-4 py-2 rounded-pill"
                >
                  <i className="bi bi-house-door me-2"></i>
                  Home
                </Link>

                <Link
                  to="/donation"
                  className="btn btn-warning text-white px-4 py-2 rounded-pill"
                >
                  <i className="bi bi-heart me-2"></i>
                  Donate Again
                </Link>
              </div>
            </div>

            <div
              className="text-center text-white py-3"
              style={{ backgroundColor: "#4b0000" }}
            >
              <p className="mb-0 small">
                © {new Date().getFullYear()} Sydney Sri Ayyappa. All Rights
                Reserved.
              </p>
              <p className="mb-0 small">
                Powered by{" "}
                <a
                  href="https://www.adada.com.au/"
                  className="text-white text-decoration-none"
                  target="_blank"
                  rel="noreferrer"
                >
                  ADADA Digital
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonationSuccess;